import React, { useState, useRef } from 'react';
import {
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Switch,
  Button,
  Grid,
  Box,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  GetApp as DownloadIcon,
  RestartAlt as ResetIcon
} from '@mui/icons-material';
import ApiService from '../services/apiService';

const ImportExportTab = () => {
  // State für Export-Optionen
  const [exportOptions, setExportOptions] = useState({
    currentFee: true,
    paymentMethods: true,
    paymentDistribution: false,
    competitors: true,
    packages: true,
    payoutConditions: true,
    cartValues: true,
    generalValues: true,
    newFee: true
  });

  // State für Import-Optionen
  const [importOptions, setImportOptions] = useState({
    currentFee: true,
    paymentMethods: true,
    paymentDistribution: false,
    competitors: true,
    packages: true,
    payoutConditions: true,
    cartValues: true,
    generalValues: true,
    newFee: true
  });

  // State für Reset-Optionen
  const [resetOptions, setResetOptions] = useState({
    currentFee: false,
    paymentMethods: false,
    competitors: false,
    payoutConditions: false,
    cartValues: false,
    generalValues: false,
    newFee: false
  });

  // State für Dialoge und Benachrichtigungen
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Ref für den File-Input
  const fileInputRef = useRef(null);

  // Handler für Export-Optionen
  const handleExportOptionChange = (option) => (event) => {
    setExportOptions({
      ...exportOptions,
      [option]: event.target.checked
    });
  };

  // Handler für Import-Optionen
  const handleImportOptionChange = (option) => (event) => {
    setImportOptions({
      ...importOptions,
      [option]: event.target.checked
    });
  };

  // Handler für Reset-Optionen
  const handleResetOptionChange = (option) => (event) => {
    setResetOptions({
      ...resetOptions,
      [option]: event.target.checked
    });
  };

  // Handler für Datei-Export
  const handleExport = () => {
    try {
      // Überprüfe, ob mindestens eine Option ausgewählt wurde
      const hasSelection = Object.values(exportOptions).some(value => value === true);
      if (!hasSelection) {
        setSnackbar({
          open: true,
          message: 'Bitte wählen Sie mindestens eine Datenart für den Export aus',
          severity: 'warning'
        });
        return;
      }
      
      // Erzeuge Export-Datei
      const dataUri = ApiService.createExportFile(exportOptions);
      
      // Erzeuge einen Download-Link
      const downloadLink = document.createElement("a");
      downloadLink.href = dataUri;
      downloadLink.download = `NIGHTOWL_Export_${new Date().toISOString().slice(0, 10)}.json`;
      
      // Füge den Link zum Dokument hinzu und klicke ihn
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Erfolgsbenachrichtigung anzeigen
      setSnackbar({
        open: true,
        message: 'Daten wurden erfolgreich exportiert',
        severity: 'success'
      });
    } catch (error) {
      console.error('Fehler beim Exportieren der Daten:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Exportieren der Daten',
        severity: 'error'
      });
    }
  };

  // Handler für Datei-Import
  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  // Handler für ausgewählte Datei
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Überprüfe, ob mindestens eine Option ausgewählt wurde
    const hasSelection = Object.values(importOptions).some(value => value === true);
    if (!hasSelection) {
      setSnackbar({
        open: true,
        message: 'Bitte wählen Sie mindestens eine Datenart für den Import aus',
        severity: 'warning'
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const success = ApiService.importData(data, importOptions);
        
        if (success) {
          setSnackbar({
            open: true,
            message: 'Daten wurden erfolgreich importiert',
            severity: 'success'
          });
          // File-Input zurücksetzen
          event.target.value = '';
        } else {
          throw new Error('Import fehlgeschlagen');
        }
      } catch (error) {
        console.error('Fehler beim Importieren der Daten:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Importieren der Daten. Stellen Sie sicher, dass die Datei ein gültiges JSON-Format hat.',
          severity: 'error'
        });
      }
    };
    
    reader.onerror = () => {
      setSnackbar({
        open: true,
        message: 'Fehler beim Lesen der Datei',
        severity: 'error'
      });
    };
    
    reader.readAsText(file);
  };

  // Handler für Reset-Dialog öffnen
  const handleResetDialogOpen = () => {
    // Überprüfe, ob mindestens eine Option ausgewählt wurde
    const hasSelection = Object.values(resetOptions).some(value => value === true);
    if (!hasSelection) {
      setSnackbar({
        open: true,
        message: 'Bitte wählen Sie mindestens eine Datenart zum Zurücksetzen aus',
        severity: 'warning'
      });
      return;
    }
    
    setResetDialogOpen(true);
  };

  // Handler für Reset bestätigen
  const handleResetConfirm = () => {
    try {
      const success = ApiService.resetToDefaults(resetOptions);
      if (success) {
        setSnackbar({
          open: true,
          message: 'Daten wurden erfolgreich zurückgesetzt',
          severity: 'success'
        });
      } else {
        throw new Error('Reset fehlgeschlagen');
      }
    } catch (error) {
      console.error('Fehler beim Zurücksetzen der Daten:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Zurücksetzen der Daten',
        severity: 'error'
      });
    } finally {
      setResetDialogOpen(false);
    }
  };

  // Handler für Snackbar schließen
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Import/Export Einstellungen
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Hier können Sie ausgewählte Daten exportieren, importieren oder auf Standardwerte zurücksetzen.
        </Alert>
        
        <Grid container spacing={3}>
          {/* Export-Bereich */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Export
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Wählen Sie die Daten aus, die exportiert werden sollen:
              </Typography>
              
              <FormGroup>
                <FormControlLabel 
                  control={<Switch checked={exportOptions.currentFee} onChange={handleExportOptionChange('currentFee')} />}
                  label="NIGHTOWL Gebühren" 
                />
                <FormControlLabel 
                  control={<Switch checked={exportOptions.paymentMethods} onChange={handleExportOptionChange('paymentMethods')} />}
                  label="Zahlungsmethoden" 
                />
                <FormControlLabel 
                  control={<Switch checked={exportOptions.paymentDistribution} onChange={handleExportOptionChange('paymentDistribution')} />}
                  label="Zahlungsmethoden-Verteilung" 
                />
                <FormControlLabel 
                  control={<Switch checked={exportOptions.competitors} onChange={handleExportOptionChange('competitors')} />}
                  label="Wettbewerber" 
                />
                <FormControlLabel 
                  control={<Switch checked={exportOptions.payoutConditions} onChange={handleExportOptionChange('payoutConditions')} />}
                  label="Auszahlungseinstellungen" 
                />
                <FormControlLabel 
                  control={<Switch checked={exportOptions.cartValues} onChange={handleExportOptionChange('cartValues')} />}
                  label="Warenkorb-Werte" 
                />
                <FormControlLabel 
                  control={<Switch checked={exportOptions.generalValues} onChange={handleExportOptionChange('generalValues')} />}
                  label="Allgemeine Werte" 
                />
                <FormControlLabel 
                  control={<Switch checked={exportOptions.newFee} onChange={handleExportOptionChange('newFee')} />}
                  label="Neue Gebühren" 
                />
              </FormGroup>
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  fullWidth
                >
                  Exportieren
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Import-Bereich */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Import
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Wählen Sie die Daten aus, die importiert werden sollen:
              </Typography>
              
              <FormGroup>
                <FormControlLabel 
                  control={<Switch checked={importOptions.currentFee} onChange={handleImportOptionChange('currentFee')} />}
                  label="NIGHTOWL Gebühren" 
                />
                <FormControlLabel 
                  control={<Switch checked={importOptions.paymentMethods} onChange={handleImportOptionChange('paymentMethods')} />}
                  label="Zahlungsmethoden" 
                />
                <FormControlLabel 
                  control={<Switch checked={importOptions.paymentDistribution} onChange={handleImportOptionChange('paymentDistribution')} />}
                  label="Zahlungsmethoden-Verteilung" 
                />
                <FormControlLabel 
                  control={<Switch checked={importOptions.competitors} onChange={handleImportOptionChange('competitors')} />}
                  label="Wettbewerber" 
                />
                <FormControlLabel 
                  control={<Switch checked={importOptions.payoutConditions} onChange={handleImportOptionChange('payoutConditions')} />}
                  label="Auszahlungseinstellungen" 
                />
                <FormControlLabel 
                  control={<Switch checked={importOptions.cartValues} onChange={handleImportOptionChange('cartValues')} />}
                  label="Warenkorb-Werte" 
                />
                <FormControlLabel 
                  control={<Switch checked={importOptions.generalValues} onChange={handleImportOptionChange('generalValues')} />}
                  label="Allgemeine Werte" 
                />
                <FormControlLabel 
                  control={<Switch checked={importOptions.newFee} onChange={handleImportOptionChange('newFee')} />}
                  label="Neue Gebühren" 
                />
              </FormGroup>
              
              <Box sx={{ mt: 2 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <Button 
                  variant="contained" 
                  color="secondary" 
                  startIcon={<UploadFileIcon />}
                  onClick={handleImportClick}
                  fullWidth
                >
                  Importieren
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Reset-Bereich */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Zurücksetzen
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Wählen Sie die Daten aus, die auf Standardwerte zurückgesetzt werden sollen:
              </Typography>
              
              <FormGroup>
                <FormControlLabel 
                  control={<Switch checked={resetOptions.currentFee} onChange={handleResetOptionChange('currentFee')} />}
                  label="NIGHTOWL Gebühren" 
                />
                <FormControlLabel 
                  control={<Switch checked={resetOptions.paymentMethods} onChange={handleResetOptionChange('paymentMethods')} />}
                  label="Zahlungsmethoden" 
                />
                <FormControlLabel 
                  control={<Switch checked={resetOptions.competitors} onChange={handleResetOptionChange('competitors')} />}
                  label="Wettbewerber & Pakete" 
                />
                <FormControlLabel 
                  control={<Switch checked={resetOptions.payoutConditions} onChange={handleResetOptionChange('payoutConditions')} />}
                  label="Auszahlungseinstellungen" 
                />
                <FormControlLabel 
                  control={<Switch checked={resetOptions.cartValues} onChange={handleResetOptionChange('cartValues')} />}
                  label="Warenkorb-Werte" 
                />
                <FormControlLabel 
                  control={<Switch checked={resetOptions.generalValues} onChange={handleResetOptionChange('generalValues')} />}
                  label="Allgemeine Werte" 
                />
                <FormControlLabel 
                  control={<Switch checked={resetOptions.newFee} onChange={handleResetOptionChange('newFee')} />}
                  label="Neue Gebühren" 
                />
              </FormGroup>
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="outlined" 
                  color="error" 
                  startIcon={<ResetIcon />}
                  onClick={handleResetDialogOpen}
                  fullWidth
                >
                  Zurücksetzen
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Reset-Bestätigungsdialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
      >
        <DialogTitle>
          Daten zurücksetzen
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie die ausgewählten Daten auf die Standardwerte zurücksetzen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetDialogOpen(false)}
            color="inherit"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleResetConfirm}
            variant="contained" 
            color="error"
          >
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Benachrichtigungen */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImportExportTab;
